import { CheckOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Flex, Select, theme as Theme, Typography } from 'antd';
import PropTypes from 'prop-types';
import { type ReactNode, useEffect, useRef, useState } from 'react';

interface StyledSelectProps {
  containerColor: string;
}

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => !['containerColor'].includes(prop),
})<StyledSelectProps>`
  &.ant-select {
    flex-shrink: 0;
  }
  &.ant-select.selected > .ant-select-selector {
    //background: ${({ containerColor }) => containerColor};
    border-color: ${({ containerColor }) => containerColor};
  }
  &
    .ant-select-selection-overflow-item.ant-select-selection-overflow-item-rest
    .ant-select-selection-item {
    border: none;
    background: transparent;
  }
`;

interface FilterSelectProps {
  mode?: 'single' | 'multiple';
  block?: boolean;
  canSelectAll?: boolean;
  disabled?: boolean;
  value?: any;
  options?: any[];
  onChange: (value: any) => void;
  optionLabelProp?: string;
  optionValueProp?: string;
  title?: string;
  link?: boolean;
  defaultOpen?: boolean;
  autoFocus?: boolean;
  selectFirstOptionOnMount?: boolean;
}

const FilterSelect = (props: FilterSelectProps): ReactNode => {
  const {
    mode,
    block,
    canSelectAll = false,
    value,
    disabled = false,
    options = [],
    onChange,
    optionLabelProp = 'label',
    optionValueProp = 'value',
    title,
    link = false,
    defaultOpen = false,
    autoFocus = false,
    selectFirstOptionOnMount = true,
  } = props;
  const isMounted = useRef(false);
  const { token } = Theme.useToken();
  const selectAllOption = {
    [optionLabelProp]: 'All',
    [optionValueProp]: -1,
    isMatching: (val: any) =>
      JSON.stringify(val) === JSON.stringify([selectAllOption[optionValueProp]]),
  };
  const [didSelectMoreThanOne, setSelectedMoreThanOne] = useState(false);
  const defaultValue = useRef(
    canSelectAll
      ? [selectAllOption[optionValueProp]]
      : selectFirstOptionOnMount
      ? [options[0]?.[optionValueProp]]
      : []
  );
  const [isSelected, setSelected] = useState(false);
  const isSelecting = useRef(value != null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    isSelecting.current = false;
    setSelected(
      (mode === 'single' && !canSelectAll) ||
        (value != null && (mode === 'multiple' ? value : [value])[0] !== defaultValue.current[0])
    );
    setSelectedMoreThanOne(mode === 'multiple' && value?.length > 1);
  }, [value]);

  useEffect(() => {
    defaultValue.current = canSelectAll
      ? [selectAllOption[optionValueProp]]
      : selectFirstOptionOnMount
      ? [options[0]?.[optionValueProp]]
      : [];
  }, [options]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, []);

  const onSelect = (selectedValue: any): void => {
    if (isSelecting.current) {
      return;
    }
    isSelecting.current = true;
    let nextValue;
    if (selectAllOption.isMatching([selectedValue]) || selectAllOption.isMatching(value)) {
      nextValue = [selectedValue];
    } else {
      nextValue = mode === 'multiple' ? [selectedValue].concat(value ?? []) : [selectedValue];
    }
    const nextSelected = nextValue[0] !== defaultValue.current[0];
    isSelecting.current = false;
    setSelected(nextSelected);
    setSelectedMoreThanOne(nextValue.length > 1);
    onChange(
      selectAllOption.isMatching(nextValue) ? null : mode === 'multiple' ? nextValue : nextValue[0]
    );
    if (mode === 'single') {
      setOpen(false);
    }
  };

  const onDeselect = (deselectedValue: any): void => {
    if (isSelecting.current || selectAllOption.isMatching([deselectedValue])) {
      return;
    }
    isSelecting.current = true;
    let nextValue;
    if (mode === 'single' || value?.length === 1) {
      nextValue = [selectAllOption[optionValueProp]];
    } else {
      nextValue = value?.filter((item: any) => item !== deselectedValue);
    }
    setSelected(nextValue[0] !== defaultValue.current[0]);
    setSelectedMoreThanOne(nextValue.length > 1);
    onChange(
      selectAllOption.isMatching(nextValue) ? null : mode === 'multiple' ? nextValue : nextValue[0]
    );
    if (mode === 'single') {
      setOpen(false);
    }
  };

  const searchOptions = (input: string, option: any): boolean =>
    (option?.[optionLabelProp] ?? '').toLowerCase().includes(input.toLowerCase());

  return (
    <StyledSelect
      allowClear
      autoFocus={autoFocus}
      defaultOpen={defaultOpen}
      disabled={disabled}
      containerColor={token.colorPrimaryBg}
      className={isSelected ? 'selected' : ''}
      showSearch={!link}
      variant={link ? 'borderless' : 'outlined'}
      style={{
        minWidth: link ? 1 : 200,
        width: block ? '100%' : undefined,
      }}
      maxTagCount={didSelectMoreThanOne ? 0 : 1}
      autoClearSearchValue={true}
      mode="multiple"
      open={open}
      onOpenChange={setOpen}
      popupMatchSelectWidth={false}
      value={value == null ? defaultValue.current : mode === 'multiple' ? value : [value]}
      onSelect={onSelect}
      onDeselect={onDeselect}
      placeholder={title}
      options={canSelectAll ? ([] as any[]).concat([selectAllOption], options) : options}
      fieldNames={{ label: optionLabelProp, value: optionValueProp }}
      optionLabelProp={optionLabelProp}
      filterOption={searchOptions}
      menuItemSelectedIcon={
        <CheckOutlined style={{ color: token.colorPrimary, paddingLeft: token.paddingXS }} />
      }
      maxTagPlaceholder={
        <Flex style={{ paddingBlock: 0 }}>
          {/* {!link ? <Typography.Text type="secondary">{title}</Typography.Text> : null} */}
          <Typography.Text strong={!link} style={{ color: token.colorPrimary, marginBlock: 0 }}>
            {value?.length} selected
          </Typography.Text>
        </Flex>
      }
      tagRender={({ label }) => (
        <Flex
          gap="small"
          align="center"
          style={{ marginRight: 3, paddingLeft: token.paddingXS, paddingBlock: 0 }}
        >
          {!link ? <Typography.Text type="secondary">{title}</Typography.Text> : null}
          <Typography.Text
            strong={!link}
            style={{ color: isSelected ? token.colorPrimary : undefined, marginBlock: 0 }}
          >
            {label}
          </Typography.Text>
        </Flex>
      )}
      onClear={() => {
        onSelect(defaultValue.current[0]);
      }}
    />
  );
};

FilterSelect.propTypes = {
  mode: PropTypes.oneOf(['single', 'multiple']),
  block: PropTypes.bool,
  canSelectAll: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  ]),
  options: PropTypes.array,
  onChange: PropTypes.func,
  optionLabelProp: PropTypes.string,
  optionValueProp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string.isRequired,
  link: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

FilterSelect.defaultProps = {
  mode: 'multiple',
  block: false,
  canSelectAll: false,
  optionLabelProp: 'label',
  optionValueProp: 'value',
  value: null,
  options: [],
  onChange: () => {},
  link: false,
  defaultOpen: false,
  autoFocus: false,
};

export default FilterSelect;
